import React from 'react';

export const DispersionChart = ({ shots }) => {
  if (!shots || shots.length === 0) return null;

  // Chart dimensions
  const width = 280;
  const height = 200;
  const padding = { top: 20, right: 20, bottom: 30, left: 40 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Calculate lateral deviation - use best available data
  const calculateLateral = (s) => {
    const carry = s.carry_distance || 200;
    if (s.offline_distance != null) return s.offline_distance;
    if (s.face_angle != null && s.club_path != null) {
      const faceToPath = s.face_angle - s.club_path;
      return faceToPath * carry / 50;
    }
    if (s.spin_axis != null) return s.spin_axis * carry / 50;
    if (s.side_spin != null) return s.side_spin / 100;
    return 0;
  };

  const shotsWithDeviation = shots.map(s => ({
    ...s,
    lateral: calculateLateral(s),
    carry: s.carry_distance || 0
  })).filter(s => s.carry > 0);

  if (shotsWithDeviation.length === 0) return <div className="text-zinc-600 text-xs text-center">No carry distance data</div>;

  // Find ranges
  const carries = shotsWithDeviation.map(s => s.carry);
  const laterals = shotsWithDeviation.map(s => s.lateral);
  const minCarry = Math.min(...carries) - 10;
  const maxCarry = Math.max(...carries) + 10;
  const maxLateral = Math.max(Math.abs(Math.min(...laterals)), Math.abs(Math.max(...laterals)), 15);

  // Scale functions
  const scaleX = (lat) => padding.left + ((lat + maxLateral) / (maxLateral * 2)) * chartWidth;
  const scaleY = (carry) => padding.top + chartHeight - ((carry - minCarry) / (maxCarry - minCarry)) * chartHeight;

  // Stats
  const avgCarry = (carries.reduce((a, b) => a + b, 0) / carries.length).toFixed(0);
  const avgLateral = (laterals.reduce((a, b) => a + b, 0) / laterals.length).toFixed(1);
  const spread = (Math.max(...laterals) - Math.min(...laterals)).toFixed(1);

  return (
    <div className="flex flex-col items-center">
      <svg width={width} height={height} className="bg-zinc-950 rounded">
        {/* Grid lines */}
        <line x1={scaleX(0)} y1={padding.top} x2={scaleX(0)} y2={height - padding.bottom} stroke="#3f3f46" strokeDasharray="4,4" />
        <line x1={padding.left} y1={scaleY(avgCarry)} x2={width - padding.right} y2={scaleY(avgCarry)} stroke="#3f3f46" strokeDasharray="2,2" />

        {/* Axis labels */}
        <text x={padding.left - 5} y={padding.top + 10} fill="#71717a" fontSize="9" textAnchor="end">{maxCarry.toFixed(0)}</text>
        <text x={padding.left - 5} y={height - padding.bottom} fill="#71717a" fontSize="9" textAnchor="end">{minCarry.toFixed(0)}</text>
        <text x={padding.left} y={height - 8} fill="#71717a" fontSize="9" textAnchor="middle">Draw</text>
        <text x={width - padding.right} y={height - 8} fill="#71717a" fontSize="9" textAnchor="middle">Fade</text>
        <text x={width / 2} y={height - 8} fill="#71717a" fontSize="9" textAnchor="middle">0</text>

        {/* Shot dots */}
        {shotsWithDeviation.map((s, i) => (
          <circle
            key={i}
            cx={scaleX(s.lateral)}
            cy={scaleY(s.carry)}
            r={5}
            fill={s.carry === Math.max(...carries) ? "#10b981" : "#3b82f6"}
            opacity={0.8}
            stroke={s.carry === Math.max(...carries) ? "#34d399" : "#60a5fa"}
            strokeWidth={1}
          />
        ))}

        {/* Average marker */}
        <circle cx={scaleX(avgLateral)} cy={scaleY(avgCarry)} r={3} fill="#fbbf24" stroke="#fcd34d" strokeWidth={1} />
      </svg>

      {/* Stats below chart */}
      <div className="flex gap-4 mt-2 text-[10px] text-zinc-500">
        <span>Avg: <span className="text-zinc-300">{avgCarry} yds</span></span>
        <span>Bias: <span className={`${avgLateral > 0 ? 'text-amber-400' : avgLateral < 0 ? 'text-blue-400' : 'text-zinc-300'}`}>{avgLateral > 0 ? '+' : ''}{avgLateral} yds</span></span>
        <span>Spread: <span className="text-zinc-300">{spread} yds</span></span>
      </div>
    </div>
  );
};
